// Utilidades DOM y helpers básicos
// Todas las funciones incluyen comentarios JSDoc para clarificar propósito y uso.

/**
 * Devuelve el primer elemento que coincide con el selector dentro del scope dado.
 * @param {string} selector - Selector CSS.
 * @param {ParentNode} [scope=document] - Nodo raíz donde buscar.
 * @returns {Element|null}
 */
export function qs(selector, scope = document) {
  return scope.querySelector(selector);
}

/**
 * Devuelve un Array de elementos que coinciden con el selector dentro del scope dado.
 * @param {string} selector - Selector CSS.
 * @param {ParentNode} [scope=document] - Nodo raíz donde buscar.
 * @returns {Element[]}
 */
export function qsa(selector, scope = document) {
  return Array.from(scope.querySelectorAll(selector));
}

/**
 * Añade un listener y devuelve una función para desuscribirlo.
 * @param {EventTarget} target
 * @param {string} type
 * @param {(e: Event) => void} handler
 * @param {AddEventListenerOptions|boolean} [options]
 * @returns {() => void} Función para eliminar el listener.
 */
export function on(target, type, handler, options) {
  target.addEventListener(type, handler, options);
  return () => target.removeEventListener(type, handler, options);
}

/**
 * Delegación de eventos: ejecuta el handler si el objetivo hace match con el selector.
 * @param {Element|Document} root - Contenedor donde escuchar.
 * @param {string} selector - Selector del elemento objetivo.
 * @param {string} type - Tipo de evento (p.ej. 'click').
 * @param {(e: Event) => void} handler - Callback a ejecutar.
 * @returns {() => void} Función para eliminar el listener delegado.
 */
export function delegate(root, selector, type, handler) {
  return on(root, type, (event) => {
    const potential = event.target.closest(selector);
    if (root.contains(potential)) {
      handler.call(potential, event);
    }
  });
}

/**
 * Devuelve una función debounced: retrasa la ejecución hasta que pasan 'wait' ms sin invocarse.
 * Útil para inputs, resize, scroll, etc.
 * @param {(…args:any[]) => void} fn
 * @param {number} [wait=150]
 * @returns {(…args:any[]) => void}
 */
export function debounce(fn, wait = 150) {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(null, args), wait);
  };
}

/**
 * Devuelve una función throttled: garantiza como mucho 1 ejecución cada 'wait' ms.
 * Útil para scroll/resize frecuentes.
 * @param {(…args:any[]) => void} fn
 * @param {number} [wait=150]
 * @returns {(…args:any[]) => void}
 */
export function throttle(fn, wait = 150) {
  let last = 0;
  return (...args) => {
    const now = Date.now();
    if (now - last >= wait) {
      last = now;
      fn.apply(null, args);
    }
  };
}

/**
 * Habilita navegación en enlaces que hayan sido interceptados con preventDefault por otros scripts.
 * Caso de uso: Elementor u otros capturan clicks para lightbox/anclas y evitan navegar.
 * Marca los enlaces con la clase 'js-allow-nav' (o personaliza el selector) para permitir la navegación.
 *
 * @param {{selector?: string, openInNewTab?: boolean}} [opts]
 *  - selector: CSS selector de los <a> objetivo (por defecto 'a.js-allow-nav').
 *  - openInNewTab: si true, abrirá en nueva pestaña cuando detecte preventDefault.
 * @returns {() => void} Función para desactivar el helper.
 *
 * Ejemplo de uso:
 * enableAllowedNavigation(); // habilita para <a class=\"js-allow-nav\">
 * // o personalizado:
 * enableAllowedNavigation({ selector: '.mi-enlace a' });
 */
export function enableAllowedNavigation(opts = {}) {
  const { selector = 'a.js-allow-nav', openInNewTab = false } = opts;
  const handler = (e) => {
    /** @type {HTMLAnchorElement|null} */
    const a = e.target && e.target.closest ? e.target.closest(selector) : null;
    if (!a) return;
    // Solo click principal sin modificadores
    const primaryNoMods = e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
    if (primaryNoMods && e.defaultPrevented) {
      if (openInNewTab || a.target === '_blank') {
        window.open(a.href, '_blank');
      } else {
        window.location.href = a.href;
      }
    }
  };
  // Escuchamos en burbujeo; si otro script hace stopPropagation no podremos interceptarlo (caso raro).
  document.addEventListener('click', handler, false);
  return () => document.removeEventListener('click', handler, false);
}


